/**
 * Development-friendly logger that maintains console visibility during development
 * but handles production logging safely
 */

const isDevelopment = process.env.NODE_ENV === 'development';
const isStaging = process.env.REACT_APP_ENV === 'staging';

class Logger {
  constructor(context = 'App') {
    this.context = context;
  }

  // Debug level - only shows in development
  debug(message, data = null) {
    if (isDevelopment) {
      console.log(`[DEBUG] [${this.context}] ${message}`, data || '');
    }
  }

  // Info level - shows in development and staging
  info(message, data = null) {
    if (isDevelopment || isStaging) {
      console.info(`[INFO] [${this.context}] ${message}`, data || '');
    }
  }

  // Warning level - shows in all environments
  warn(message, data = null) {
    console.warn(`[WARN] [${this.context}] ${message}`, data || '');
  }

  // Error level - shows in all environments
  error(message, error = null) {
    console.error(`[ERROR] [${this.context}] ${message}`, error || '');
  }

  // Sensitive data logging - only in development, sanitized data only
  sensitive(message, data = null) {
    if (isDevelopment) {
      // In development, show limited sensitive data for debugging
      const sanitizedData = this.sanitizeData(data);
      console.log(`[SENSITIVE] [${this.context}] ${message}`, sanitizedData);
    }
    // In production/staging, this does nothing
  }

  // Sanitize sensitive data for logging
  sanitizeData(data) {
    if (!data) return data;

    const sensitiveFields = ['password', 'token', 'authorization', 'secret', 'key'];

    if (typeof data === 'object') {
      const sanitized = { ...data };

      Object.keys(sanitized).forEach(key => {
        if (sensitiveFields.some(field => key.toLowerCase().includes(field))) {
          sanitized[key] = '[REDACTED]';
        }
      });

      return sanitized;
    }

    return data;
  }
}

// Create logger instances for different parts of the app
export const createLogger = (context) => new Logger(context);

// Default logger
export const logger = new Logger('App');

// Specific loggers for common use cases
export const storeLogger = new Logger('Store');
export const apiLogger = new Logger('API');
export const authLogger = new Logger('Auth');

export default logger;
